import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComments, faChartLine, faFilter, faDownload, faCalendarAlt, faExpand, faTimes, faInfoCircle, faBrain, faLightbulb, faExclamationTriangle, faArrowTrendUp, faHeart, faThumbsUp, faSmile, faFrown, faMeh, faClock, faCheckCircle, faExclamationCircle, faStar, faSpinner, faCheck } from '@fortawesome/free-solid-svg-icons';
import { faInstagram, faFacebook, faTwitter, faLinkedin, faTiktok } from '@fortawesome/free-brands-svg-icons';
import { useEngagement } from '../../context/EngagementContext';
import { useAuth } from '../../context/AuthContext';
import { engagementAPI, creditsAPI, analysisJobsAPI, businessAuthAPI } from '../../services/api';
import { useTranslation } from 'react-i18next';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BubbleController,
  BubbleDataPoint
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BubbleController
);

const EngagementPage: React.FC = () => {
  // @ts-ignore - react-i18next supports namespace parameter but TypeScript types don't reflect it
  const { t, i18n } = useTranslation('dashboard');
  const { state, fetchSentimentData, fetchResponseTimeData, fetchProblemSolvingData, fetchFeedbackTypeData, fetchTrendData, fetchFeedbacksData, fetchAIAnalysis, fetchAllEngagementData } = useEngagement();
  const { business, refreshBusinessProfile } = useAuth();
  const [selectedPeriod, setSelectedPeriod] = useState('30');
  const [hoveredTooltip, setHoveredTooltip] = useState<string | null>(null);
  const [modalChart, setModalChart] = useState<string | null>(null);
  
  // Check analysis status from business model
  const analysisStatus = business?.analysisStatus?.['social-media-engagement']?.status || 'noanalyze';
  const isAnalysisPending = analysisStatus === 'pending';
  const isAnalysisCompleted = analysisStatus === 'completed';
  const isAnalysisFailed = analysisStatus === 'failed';
  const hasAnyAnalysis = isAnalysisCompleted;
  
  // Analysis start flow states
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [estimateLoading, setEstimateLoading] = useState(false);
  const [estimatedCredits, setEstimatedCredits] = useState<number | null>(null);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  // Social media username setup states
  const [platforms, setPlatforms] = useState<Record<string, { username: string; loading: boolean; profile: any; error: string | null; verified: boolean; lastVerifiedUsername?: string | null }>>({
    facebook: { username: '', loading: false, profile: null, error: null, verified: false, lastVerifiedUsername: null },
    instagram: { username: '', loading: false, profile: null, error: null, verified: false, lastVerifiedUsername: null },
    twitter: { username: '', loading: false, profile: null, error: null, verified: false, lastVerifiedUsername: null },
    tiktok: { username: '', loading: false, profile: null, error: null, verified: false, lastVerifiedUsername: null },
    linkedin: { username: '', loading: false, profile: null, error: null, verified: false, lastVerifiedUsername: null },
  });
  const [verifying, setVerifying] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [setupError, setSetupError] = useState<string | null>(null);

  // Chart refs
  const responseTimeChartRef = useRef<HTMLCanvasElement | null>(null);
  const problemSolvingChartRef = useRef<HTMLCanvasElement | null>(null);
  const feedbackTypeChartRef = useRef<HTMLCanvasElement | null>(null);
  const trendChartRef = useRef<HTMLCanvasElement | null>(null);

  // Modal chart refs
  const modalResponseTimeChartRef = useRef<HTMLCanvasElement | null>(null);
  const modalProblemSolvingChartRef = useRef<HTMLCanvasElement | null>(null);
  const modalFeedbackTypeChartRef = useRef<HTMLCanvasElement | null>(null);
  const modalTrendChartRef = useRef<HTMLCanvasElement | null>(null);

  const chartInstancesRef = useRef<{ [key: string]: ChartJS | null }>({});

  // Generate mock data
  const generateData = (days: number) => {
    const labels = [];
    const now = new Date();
    
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      labels.push(date.toLocaleDateString(i18n.language === 'tr' ? 'tr-TR' : 'en-US', { month: 'short', day: 'numeric' }));
    }

    // Sentiment data for different platforms
    const sentimentData = {
      instagram: labels.map(() => Math.random() * 0.4 + 0.3),
      facebook: labels.map(() => Math.random() * 0.3 + 0.4),
      twitter: labels.map(() => Math.random() * 0.5 + 0.2),
      linkedin: labels.map(() => Math.random() * 0.3 + 0.5),
      total: labels.map(() => Math.random() * 0.2 + 0.4)
    };

    return { labels, sentimentData };
  };



  // AI Analysis data
  const aiAnalyses = {
    sentiment: {
      insights: [
        {
          type: 'insight',
          title: t('socialMedia.engagement.aiInsights.sentiment.engagementSentimentAnalysis'),
          message: t('socialMedia.engagement.aiInsights.sentiment.engagementSentimentMessage'),
          icon: faLightbulb,
          color: 'text-blue-400'
        },
        {
          type: 'prediction',
          title: t('socialMedia.engagement.aiInsights.sentiment.satisfactionTrendPrediction'),
          message: t('socialMedia.engagement.aiInsights.sentiment.satisfactionTrendMessage'),
          icon: faArrowTrendUp,
          color: 'text-green-400'
        },
        {
          type: 'warning',
          title: t('socialMedia.engagement.aiInsights.sentiment.twitterAttention'),
          message: t('socialMedia.engagement.aiInsights.sentiment.twitterAttentionMessage'),
          icon: faExclamationTriangle,
          color: 'text-yellow-400'
        }
      ]
    },
    responseTime: {
      insights: [
        {
          type: 'insight',
          title: t('socialMedia.engagement.aiInsights.responseTime.responseTimes'),
          message: t('socialMedia.engagement.aiInsights.responseTime.responseTimesMessage'),
          icon: faLightbulb,
          color: 'text-blue-400'
        },
        {
          type: 'prediction',
          title: t('socialMedia.engagement.aiInsights.responseTime.improvementPotential'),
          message: t('socialMedia.engagement.aiInsights.responseTime.improvementPotentialMessage'),
          icon: faArrowTrendUp,
          color: 'text-green-400'
        }
      ]
    },
    problemSolving: {
      insights: [
        {
          type: 'insight',
          title: t('socialMedia.engagement.aiInsights.problemSolving.problemSolvingRate'),
          message: t('socialMedia.engagement.aiInsights.problemSolving.problemSolvingRateMessage'),
          icon: faLightbulb,
          color: 'text-blue-400'
        },
        {
          type: 'warning',
          title: t('socialMedia.engagement.aiInsights.problemSolving.improvementArea'),
          message: t('socialMedia.engagement.aiInsights.problemSolving.improvementAreaMessage'),
          icon: faExclamationTriangle,
          color: 'text-yellow-400'
        }
      ]
    }
  };

  // Mock feedback data
  const feedbackData = {
    positive: [
      "Harika ürün, çok memnunum!",
      "Müşteri hizmetleri çok hızlı yanıt verdi.",
      "Kalite beklentilerimi aştı.",
      "Teslimat çok hızlıydı, teşekkürler!",
      "Fiyat performans oranı mükemmel."
    ],
    suggestions: [
      "Daha fazla renk seçeneği olabilir mi?",
      "Mobil uygulama daha kullanıcı dostu olabilir.",
      "Kargo takibi daha detaylı olmalı.",
      "İndirim kampanyaları daha sık olabilir.",
      "Müşteri yorumları daha görünür olmalı."
    ],
    problems: [
      "Ürün hasarlı geldi, değişim süreci uzun sürdü.",
      "Siparişim iptal edildi, bilgilendirme geç geldi.",
      "Fatura hatası var, düzeltilmesi gerekiyor.",
      "Kargo takip numarası çalışmıyor.",
      "Müşteri hizmetlerine ulaşamıyorum."
    ]
  };

  // Memoized tooltip handlers
  const handleTooltipEnter = useCallback((tooltipId: string) => {
    setHoveredTooltip(tooltipId);
  }, []);

  const handleTooltipLeave = useCallback(() => {
    setHoveredTooltip(null);
  }, []);

  // Simple Tooltip component
  const Tooltip: React.FC<{ id: string; content: string; children: React.ReactNode }> = ({ id, content, children }) => (
    <div className="relative inline-block">
      {children}
      {hoveredTooltip === id && (
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg shadow-lg z-50 w-64 text-left">
          {content}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
        </div>
      )}
    </div>
  );

  // Check if user has any social media accounts
  const hasAnySocialMediaAccount = useMemo(() => {
    if (!business) return false;
    const platformHasData = (p?: { username?: string; isConnected?: boolean }) =>
      !!(p?.username && p.username.trim()) || p?.isConnected === true;
    return platformHasData(business.facebook) || 
           platformHasData(business.instagram) || 
           platformHasData(business.twitter) || 
           platformHasData(business.tiktok) || 
           platformHasData(business.linkedin);
  }, [business]);

  // Check sync status for all connected social media accounts
  const syncStatuses = useMemo(() => {
    if (!business) return { hasAnyPending: false, allCompleted: false, hasAnyAccount: false };
    
    const accounts = [
      business.facebook,
      business.instagram,
      business.twitter,
      business.tiktok,
      business.linkedin
    ];
    
    const connectedAccounts = accounts.filter(acc => 
      acc && (!!(acc.username && acc.username.trim()) || acc.isConnected === true)
    );
    
    if (connectedAccounts.length === 0) {
      return { hasAnyPending: false, allCompleted: false, hasAnyAccount: false };
    }
    
    const hasAnyPending = connectedAccounts.some(acc => acc && acc.syncStatus === 'pending');
    const allCompleted = connectedAccounts.every(acc => acc && acc.syncStatus === 'completed');
    
    return { hasAnyPending, allCompleted, hasAnyAccount: true };
  }, [business]);

  // Handle username change
  const handleUsernameChange = (platform: string, value: string) => {
    setPlatforms(prev => ({
      ...prev,
      [platform]: {
        ...prev[platform],
        username: value,
        profile: value.trim() === prev[platform].lastVerifiedUsername ? prev[platform].profile : null,
        error: null,
        verified: value.trim() === prev[platform].lastVerifiedUsername ? prev[platform].verified : false,
      }
    }));
    setSetupError(null);
  };

  // Handle verify all
  const handleVerifyAll = async () => {
    const payload: Record<string, string> = {};
    Object.entries(platforms).forEach(([platform, state]) => {
      const trimmed = state.username.trim();
      if (trimmed && trimmed !== state.lastVerifiedUsername) {
        payload[platform] = trimmed;
      }
    });

    if (Object.keys(payload).length === 0) {
      return;
    }

    setVerifying(true);
    setPlatforms(prev => {
      const next = { ...prev };
      Object.keys(payload).forEach((platform) => {
        next[platform] = {
          ...next[platform],
          loading: true,
          error: null,
        };
      });
      return next;
    });
    setSetupError(null);

    try {
      const response = await businessAuthAPI.getSocialMediaProfiles(payload);
      const respData = response?.data?.data || {};
      const respErrors = (response?.data as any)?.errors || {};

      setPlatforms(prev => {
        const next = { ...prev };
        Object.keys(payload).forEach((platform) => {
          const platformResp = (respData as any)[platform] || null;
          if (platformResp) {
            next[platform] = {
              ...next[platform],
              loading: false,
              profile: platformResp,
              verified: true,
              error: null,
              lastVerifiedUsername: next[platform].username.trim(),
            };
          } else {
            const errorMsg = (respErrors as any)?.[platform] || t('socialMedia.engagement.profileNotFound');
            next[platform] = {
              ...next[platform],
              loading: false,
              profile: null,
              verified: false,
              error: errorMsg,
            };
          }
        });
        return next;
      });
    } catch (err: any) {
      const genericError = err?.response?.data?.message || t('socialMedia.engagement.verificationFailed');
      setPlatforms(prev => {
        const next = { ...prev };
        Object.keys(payload).forEach((platform) => {
          next[platform] = {
            ...next[platform],
            loading: false,
            profile: null,
            verified: false,
            error: err?.response?.data?.errors?.[platform] || genericError,
          };
        });
        return next;
      });
    } finally {
      setVerifying(false);
    }
  };

  // Handle submit
  const handleSubmit = async () => {
    const hasAnyUsername = Object.values(platforms).some(p => p.username.trim());
    if (!hasAnyUsername) {
      setSetupError(t('socialMedia.engagement.enterAtLeastOneUsername'));
      return;
    }

    const hasAnyVerified = Object.values(platforms).some(p => p.verified);
    if (!hasAnyVerified) {
      setSetupError(t('socialMedia.engagement.verifyAtLeastOneAccount'));
      return;
    }

    setSubmitting(true);
    setSetupError(null);

    try {
      const payload: any = {};
      Object.entries(platforms).forEach(([platform, state]) => {
        if (state.verified && state.username.trim()) {
          payload[platform] = state.username.trim();
        }
      });

      await businessAuthAPI.addSocialMediaAccounts(payload);
      await refreshBusinessProfile();
      
      try {
        void businessAuthAPI.syncSocialMediaData();
      } catch (_e) {
        // Ignore sync errors
      }
    } catch (err: any) {
      setSetupError(err.response?.data?.message || t('socialMedia.engagement.accountSaveError'));
    } finally {
      setSubmitting(false);
    }
  };

  const getProxyImageUrl = (url?: string) => {
    if (!url) return '';
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
    return `${baseUrl}/proxy-image?url=${encodeURIComponent(url)}`;
  };

  const platformConfig = {
    facebook: { icon: faFacebook, label: t('socialMedia.engagement.platforms.facebook'), placeholder: t('socialMedia.engagement.platformPlaceholders.facebook') },
    instagram: { icon: faInstagram, label: t('socialMedia.engagement.platforms.instagram'), placeholder: t('socialMedia.engagement.platformPlaceholders.instagram') },
    twitter: { icon: faTwitter, label: t('socialMedia.engagement.platforms.twitter'), placeholder: t('socialMedia.engagement.platformPlaceholders.twitter') },
    tiktok: { icon: faTiktok, label: t('socialMedia.engagement.platforms.tiktok'), placeholder: t('socialMedia.engagement.platformPlaceholders.tiktok') },
    linkedin: { icon: faLinkedin, label: t('socialMedia.engagement.platforms.linkedin'), placeholder: t('socialMedia.engagement.platformPlaceholders.linkedin') },
  };

  // Component mount olduğunda verileri yükle - only if all syncs completed and analysis exists
  useEffect(() => {
    if (syncStatuses.allCompleted && isAnalysisCompleted && business?._id) {
      // Tüm engagement verisini tek seferde çek
      fetchAllEngagementData();
    }
  }, [syncStatuses.allCompleted, isAnalysisCompleted, business?._id, fetchAllEngagementData]);

  // AI Analysis component
  const AIAnalysis: React.FC<{ chartType: string }> = ({ chartType }) => {
    const analysis = state.aiAnalysis[chartType];
    const isLoading = state.loading.aiAnalysis[chartType];
    const error = state.error.aiAnalysis[chartType];
    
    // Debug: AI Analysis state'ini kontrol et
    console.log(`AI Analysis [${chartType}]:`, { analysis, isLoading, error, hasInsights: analysis?.insights?.length });
    
    // Loading durumu
    if (isLoading) {
      return (
        <div className="mt-6">
          <div className="flex items-center justify-center py-8">
            <div className="text-white/70">{t('socialMedia.engagement.aiAnalysisLoading')}</div>
          </div>
        </div>
      );
    }
    
    // Önce analysis var mı kontrol et - eğer varsa göster
    if (analysis && analysis.insights && analysis.insights.length > 0) {
      return (
        <div className="mt-6 space-y-3">
          <div className="flex items-center space-x-2 mb-4">
            <FontAwesomeIcon icon={faBrain} className="text-purple-400 text-lg" />
            <h4 className="text-lg font-semibold text-white">{t('socialMedia.engagement.aiAnalysisTitle')}</h4>
          </div>
          <div className="space-y-3">
            {analysis.insights.map((insight, index) => (
              <div key={index} className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl p-4">
                <div className="flex items-start space-x-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    insight.type === 'insight' ? 'bg-blue-500/20' :
                    insight.type === 'prediction' ? 'bg-green-500/20' :
                    insight.type === 'recommendation' ? 'bg-purple-500/20' :
                    insight.type === 'warning' ? 'bg-yellow-500/20' :
                    'bg-gray-500/20'
                  }`}>
                    <FontAwesomeIcon 
                      icon={
                        insight.type === 'insight' ? faLightbulb :
                        insight.type === 'prediction' ? faArrowTrendUp :
                        insight.type === 'recommendation' ? faBrain :
                        insight.type === 'warning' ? faExclamationTriangle :
                        faInfoCircle
                      } 
                      className={`text-sm ${
                        insight.type === 'insight' ? 'text-blue-400' :
                        insight.type === 'prediction' ? 'text-green-400' :
                        insight.type === 'recommendation' ? 'text-purple-400' :
                        insight.type === 'warning' ? 'text-yellow-400' :
                        'text-gray-400'
                      }`} 
                    />
                  </div>
                  <div className="flex-1">
                    <h5 className="text-white font-medium mb-1">{insight.title}</h5>
                    <p className="text-white/70 text-sm leading-relaxed">{insight.message}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }
    
    // PREPARING durumu - sadece analysis yoksa göster
    if (error === 'PREPARING') {
      return (
        <div className="mt-6">
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center mb-4">
              <div className="w-8 h-8 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
            </div>
            <p className="text-white/70 text-lg font-medium mb-2">{t('socialMedia.engagement.aiAnalysisPreparing')}</p>
            <p className="text-white/50 text-sm">{t('socialMedia.engagement.aiAnalysisPreparingDescription')}</p>
          </div>
        </div>
      );
    }
    
    // Hata durumu (PREPARING dışında)
    if (error && error !== 'PREPARING') {
      return (
        <div className="mt-6">
          <div className="flex items-center justify-center py-8">
            <div className="text-red-400">{t('socialMedia.engagement.aiAnalysisError')} {error}</div>
          </div>
        </div>
      );
    }
    
    // Analysis yoksa
    return null;
  };

  // Response Time Chart
  useEffect(() => {
    if (responseTimeChartRef.current) {
      const ctx = responseTimeChartRef.current.getContext('2d');
      if (ctx) {
        if (chartInstancesRef.current.responseTime) {
          chartInstancesRef.current.responseTime.destroy();
        }

        const chart = new ChartJS(ctx, {
          type: 'bar',
          data: state.responseTime || {
            labels: [],
            datasets: []
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                labels: {
                  color: 'rgba(255, 255, 255, 0.8)',
                  font: {
                    size: 12
                  }
                }
              }
            },
            scales: {
              x: {
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)'
                },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.7)',
                  font: {
                    size: 11
                  }
                }
              },
              y: {
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)'
                },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.7)',
                  font: {
                    size: 11
                  }
                }
              }
            }
          }
        });

        chartInstancesRef.current.responseTime = chart;
      }
    }
  }, [state.responseTime]);

  // Problem Solving Chart
  useEffect(() => {
    if (problemSolvingChartRef.current) {
      const ctx = problemSolvingChartRef.current.getContext('2d');
      if (ctx) {
        if (chartInstancesRef.current.problemSolving) {
          chartInstancesRef.current.problemSolving.destroy();
        }

        const chart = new ChartJS(ctx, {
          type: 'doughnut',
          data: state.problemSolving || {
            labels: [],
            datasets: []
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'bottom',
                labels: {
                  color: 'rgba(255, 255, 255, 0.8)',
                  font: {
                    size: 12
                  },
                  padding: 20
                }
              }
            }
          }
        });

        chartInstancesRef.current.problemSolving = chart;
      }
    }
  }, [state.problemSolving]);

  // Feedback Type Chart
  useEffect(() => {
    if (feedbackTypeChartRef.current) {
      const ctx = feedbackTypeChartRef.current.getContext('2d');
      if (ctx) {
        if (chartInstancesRef.current.feedbackType) {
          chartInstancesRef.current.feedbackType.destroy();
        }

        const chart = new ChartJS(ctx, {
          type: 'bar',
          data: state.feedbackType || {
            labels: [],
            datasets: []
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                labels: {
                  color: 'rgba(255, 255, 255, 0.8)',
                  font: {
                    size: 12
                  }
                }
              }
            },
            scales: {
              x: {
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)'
                },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.7)',
                  font: {
                    size: 11
                  }
                }
              },
              y: {
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)'
                },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.7)',
                  font: {
                    size: 11
                  }
                }
              }
            }
          }
        });

        chartInstancesRef.current.feedbackType = chart;
      }
    }
  }, [state.feedbackType]);

  // Trend Chart
  useEffect(() => {
    if (trendChartRef.current) {
      const ctx = trendChartRef.current.getContext('2d');
      if (ctx) {
        if (chartInstancesRef.current.trend) {
          chartInstancesRef.current.trend.destroy();
        }

        const chart = new ChartJS(ctx, {
          type: 'line',
          data: state.trend || {
            labels: [],
            datasets: []
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            interaction: {
              intersect: false,
              mode: 'index'
            },
            plugins: {
              legend: {
                labels: {
                  color: 'rgba(255, 255, 255, 0.8)',
                  font: {
                    size: 12
                  }
                }
              }
            },
            scales: {
              x: {
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)'
                },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.7)',
                  font: {
                    size: 11
                  }
                }
              },
              y: {
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)'
                },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.7)',
                  font: {
                    size: 11
                  }
                }
              }
            }
          }
        });

        chartInstancesRef.current.trend = chart;
      }
    }
  }, [state.trend]);

  // Modal chart creation functions
  const createModalChart = (chartType: string, ref: React.RefObject<HTMLCanvasElement | null>) => {
    if (!ref.current) return;

    const ctx = ref.current.getContext('2d');
    if (!ctx) return;

    // Destroy existing chart
    if (chartInstancesRef.current[`modal_${chartType}`]) {
      chartInstancesRef.current[`modal_${chartType}`]?.destroy();
    }

    let chart: ChartJS;

    switch (chartType) {
      case 'responseTime':
        chart = new ChartJS(ctx, {
          type: 'bar',
          data: state.responseTime || {
            labels: [],
            datasets: []
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                labels: {
                  color: 'rgba(255, 255, 255, 0.8)',
                  font: {
                    size: 12
                  }
                }
              }
            },
            scales: {
              x: {
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)'
                },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.7)',
                  font: {
                    size: 11
                  }
                }
              },
              y: {
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)'
                },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.7)',
                  font: {
                    size: 11
                  }
                }
              }
            }
          }
        });
        break;
      case 'problemSolving':
        chart = new ChartJS(ctx, {
          type: 'doughnut',
          data: state.problemSolving || {
            labels: [],
            datasets: []
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'bottom',
                labels: {
                  color: 'rgba(255, 255, 255, 0.8)',
                  font: {
                    size: 12
                  },
                  padding: 20
                }
              }
            }
          }
        });
        break;
      case 'feedbackType':
        chart = new ChartJS(ctx, {
          type: 'doughnut',
          data: state.feedbackType || {
            labels: [],
            datasets: []
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'bottom',
                labels: {
                  color: 'rgba(255, 255, 255, 0.8)',
                  font: {
                    size: 12
                  },
                  padding: 20
                }
              }
            }
          }
        });
        break;
      case 'trend':
        chart = new ChartJS(ctx, {
          type: 'line',
          data: state.trend || {
            labels: [],
            datasets: []
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            interaction: {
              intersect: false,
              mode: 'index'
            },
            plugins: {
              legend: {
                labels: {
                  color: 'rgba(255, 255, 255, 0.8)',
                  font: {
                    size: 12
                  }
                }
              }
            },
            scales: {
              x: {
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)'
                },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.7)',
                  font: {
                    size: 11
                  }
                }
              },
              y: {
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)'
                },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.7)',
                  font: {
                    size: 11
                  }
                }
              }
            }
          }
        });
        break;
      default:
        return;
    }

    chartInstancesRef.current[`modal_${chartType}`] = chart;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      Object.values(chartInstancesRef.current).forEach(chart => {
        if (chart) {
          chart.destroy();
        }
      });
    };
  }, []);

  // Show social media username setup if no accounts exist
  if (!hasAnySocialMediaAccount) {
    const hasAnyUsername = Object.values(platforms).some(p => p.username.trim());
    const allEnteredAreVerified = Object.values(platforms).every(p => !p.username.trim() || p.verified);
    const primaryCtaIsContinue = hasAnyUsername && allEnteredAreVerified && !verifying;

    return (
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="w-full max-w-2xl space-y-6">
          <div className="text-center">
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center mb-6 mx-auto">
              <FontAwesomeIcon icon={faComments} className="text-3xl sm:text-4xl text-white" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-4">{t('socialMedia.engagement.connectAccounts')}</h1>
            <p className="text-white/70 text-sm sm:text-base mb-8">
              {t('socialMedia.engagement.connectAccountsDescription')}
            </p>
          </div>

          <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl md:rounded-2xl p-4 md:p-6 space-y-6">
            {setupError && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm">
                {setupError}
              </div>
            )}

            <div className="space-y-4">
              {Object.entries(platformConfig).map(([platform, config]) => (
                <div key={platform} className="space-y-2">
                  <label className="flex items-center space-x-2 text-white/80 text-sm">
                    <FontAwesomeIcon icon={config.icon} className="text-lg" />
                    <span>{config.label}</span>
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      placeholder={config.placeholder}
                      value={platforms[platform].username}
                      onChange={(e) => handleUsernameChange(platform, e.target.value)}
                      className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-purple-400"
                    />
                    {platforms[platform].verified && (
                      <FontAwesomeIcon icon={faCheck} className="text-green-400" />
                    )}
                    {platforms[platform].loading && (
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin text-white/50" />
                    )}
                  </div>
                  {platforms[platform].error && (
                    <p className="text-red-400 text-xs">{platforms[platform].error}</p>
                  )}
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleVerifyAll}
                disabled={verifying || !hasAnyUsername}
                className="flex-1 px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white hover:bg-white/20 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {verifying ? (
                  <span className="flex items-center justify-center">
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                    {t('socialMedia.engagement.verifying')}
                  </span>
                ) : (
                  t('socialMedia.engagement.verifyAccounts')
                )}
              </button>
              <button
                onClick={handleSubmit}
                disabled={!primaryCtaIsContinue || submitting}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-500 to-blue-600 rounded-lg text-white font-semibold hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <span className="flex items-center justify-center">
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                    {t('socialMedia.engagement.adding')}
                  </span>
                ) : (
                  t('socialMedia.engagement.addAccounts')
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show sync pending screen if any account is syncing
  if (syncStatuses.hasAnyAccount && syncStatuses.hasAnyPending) {
    return (
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center mb-6 mx-auto">
            <div className="w-8 h-8 sm:w-10 sm:h-10 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
          </div>
          <FontAwesomeIcon icon={faComments} className="text-4xl sm:text-5xl text-purple-500 mb-4" />
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-3">{t('socialMedia.engagement.dataSyncing')}</h1>
          <p className="text-white/70 text-sm sm:text-base mb-6">
            {t('socialMedia.engagement.dataSyncingDescription')}
          </p>
          <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl p-4">
            <p className="text-white/60 text-xs sm:text-sm">
              {t('socialMedia.engagement.syncCompleteMessage')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show analysis start screen if all syncs completed but no analysis exists
  if (syncStatuses.allCompleted && !isAnalysisCompleted && !isAnalysisPending) {
    return (
      <>
        <div className="flex items-center justify-center min-h-screen px-4">
          <div className="w-full max-w-md space-y-6">
            <div className="text-center">
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center mb-6 mx-auto">
                <FontAwesomeIcon icon={faBrain} className="text-3xl sm:text-4xl text-white" />
              </div>
              <FontAwesomeIcon icon={faComments} className="text-4xl sm:text-5xl text-purple-500 mb-4" />
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-3">{t('socialMedia.engagement.startAiAnalysis')}</h1>
              <p className="text-white/70 text-sm sm:text-base mb-6">
                {t('socialMedia.engagement.engagementReady')}
              </p>
            </div>

            <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl md:rounded-2xl p-4 md:p-6 space-y-4">
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <FontAwesomeIcon icon={faLightbulb} className="text-yellow-400 mt-1" />
                  <div>
                    <p className="text-white font-medium text-sm sm:text-base">{t('socialMedia.engagement.aiAnalysisFeatures')}</p>
                    <p className="text-white/70 text-xs sm:text-sm">{t('socialMedia.engagement.detailedEngagementAnalysis')}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <FontAwesomeIcon icon={faChartLine} className="text-blue-400 mt-1" />
                  <div>
                    <p className="text-white font-medium text-sm sm:text-base">{t('socialMedia.engagement.chartsAndReports')}</p>
                    <p className="text-white/70 text-xs sm:text-sm">{t('socialMedia.engagement.visualAnalysisReports')}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <FontAwesomeIcon icon={faBrain} className="text-purple-400 mt-1" />
                  <div>
                    <p className="text-white font-medium text-sm sm:text-base">{t('socialMedia.engagement.smartRecommendations')}</p>
                    <p className="text-white/70 text-xs sm:text-sm">{t('socialMedia.engagement.aiImprovementRecommendations')}</p>
                  </div>
                </div>
              </div>

              <button
                onClick={async () => {
                  setAiModalOpen(true);
                  setEstimatedCredits(null);
                  // Set default dates (last 30 days)
                  const today = new Date();
                  const thirtyDaysAgo = new Date();
                  thirtyDaysAgo.setDate(today.getDate() - 30);
                  setEndDate(today.toISOString().split('T')[0]);
                  setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
                }}
                className="w-full bg-gradient-to-r from-purple-500 to-blue-600 px-6 py-4 rounded-xl text-white font-semibold hover:opacity-90 transition-opacity text-base sm:text-lg"
              >
                <FontAwesomeIcon icon={faBrain} className="mr-2" />
                {t('socialMedia.engagement.startAiAnalysis')}
              </button>
            </div>
          </div>
        </div>

        {/* AI Analysis Modal */}
        {aiModalOpen && (
          <div className="fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setAiModalOpen(false)}></div>
            <div className="absolute top-4 left-4 right-4 bottom-4 sm:top-10 sm:left-10 sm:right-10 sm:bottom-10 md:top-1/2 md:left-1/2 md:right-auto md:bottom-auto md:-translate-x-1/2 md:-translate-y-1/2 bg-gray-900 rounded-2xl border border-white/10 w-full max-w-md overflow-hidden">
              <div className="p-4 sm:p-6 border-b border-white/10">
                <h3 className="text-lg sm:text-xl font-bold text-white">{t('socialMedia.engagement.aiAnalysisTitle')}</h3>
              </div>
              <div className="p-4 sm:p-6 space-y-4">
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">{t('socialMedia.engagement.startDate') || 'Başlangıç Tarihi'}</label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      max={endDate || new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">{t('socialMedia.engagement.endDate') || 'Bitiş Tarihi'}</label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      min={startDate}
                      max={new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                    />
                  </div>
                </div>
                <div className="text-white/80">
                  {t('socialMedia.engagement.estimatedCreditsLabel')}
                  <div className="mt-2 text-xl sm:text-2xl font-semibold text-white">
                    {estimateLoading ? (
                      <span className="flex items-center">
                        <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                        {t('socialMedia.engagement.calculating')}
                      </span>
                    ) : (
                      estimatedCredits !== null 
                        ? Number(estimatedCredits).toLocaleString(i18n.language === 'tr' ? 'tr-TR' : 'en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
                        : '—'
                    )}
                  </div>
                </div>
                <button
                  className="w-full bg-gradient-to-r from-blue-500 to-blue-700 py-2 px-4 rounded-xl text-white font-medium hover:from-blue-600 hover:to-blue-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!startDate || !endDate}
                  onClick={async () => {
                    if (!startDate || !endDate) return;
                    setEstimateLoading(true);
                    setEstimatedCredits(null);
                    try {
                      const res = await creditsAPI.estimate('socialmediaengagement', startDate, endDate);
                      const value = res?.data?.estimatedCredits ?? res?.data?.data?.estimatedCredits ?? null;
                      setEstimatedCredits(typeof value === 'number' ? value : null);
                    } finally {
                      setEstimateLoading(false);
                    }
                  }}
                >
                  {t('socialMedia.engagement.calculateCredits') || 'Kredi Hesapla'}
                </button>
                <button
                  className="w-full bg-gradient-to-r from-purple-500 to-purple-700 py-3 px-6 rounded-xl text-white font-semibold hover:from-purple-600 hover:to-purple-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={estimateLoading || estimatedCredits === null || !startDate || !endDate}
                  onClick={async () => {
                    try {
                      if (!startDate || !endDate) return;
                      setEstimateLoading(true);
                      await analysisJobsAPI.start('socialmediaengagement', startDate, endDate);
                    } finally {
                      setEstimateLoading(false);
                      setAiModalOpen(false);
                    }
                  }}
                >
                  {t('socialMedia.engagement.spendCredits')}
                </button>
                <button
                  className="w-full mt-2 bg-white/10 border border-white/20 py-3 px-6 rounded-xl text-white hover:bg-white/20 transition"
                  onClick={() => {
                    setAiModalOpen(false);
                    setStartDate('');
                    setEndDate('');
                    setEstimatedCredits(null);
                  }}
                >
                  {t('socialMedia.engagement.cancel')}
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // Show loading while analysis is pending (only if all syncs are completed)
  if (syncStatuses.allCompleted && isAnalysisPending) {
    return (
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center mb-6 mx-auto">
            <div className="w-8 h-8 sm:w-10 sm:h-10 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
          </div>
          <FontAwesomeIcon icon={faComments} className="text-4xl sm:text-5xl text-purple-500 mb-4" />
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-3">{t('socialMedia.engagement.analysisInProgress')}</h1>
          <p className="text-white/70 text-sm sm:text-base mb-6">
            {t('socialMedia.engagement.analysisInProgressDescription')}
          </p>
          <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl p-4">
            <p className="text-white/60 text-xs sm:text-sm">
              {t('socialMedia.engagement.analysisCompleteMessage')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between space-y-4 md:space-y-0">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">{t('socialMedia.engagement.title')}</h1>
          <p className="text-white/70 text-sm md:text-base">{t('socialMedia.engagement.subtitle')}</p>
        </div>
        <button
          onClick={async () => {
            setAiModalOpen(true);
            setEstimatedCredits(null);
            // Set default dates (last 30 days)
            const today = new Date();
            const thirtyDaysAgo = new Date();
            thirtyDaysAgo.setDate(today.getDate() - 30);
            setEndDate(today.toISOString().split('T')[0]);
            setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
          }}
          className="px-3 md:px-4 py-2 rounded-xl bg-white/10 border border-white/20 text-white text-xs md:text-sm md:hover:bg-white/20 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span className="hidden sm:inline">{t('socialMedia.engagement.startAiAnalysisButton')}</span>
          <span className="sm:hidden">{t('socialMedia.engagement.aiAnalysis')}</span>
        </button>
        <div className="flex flex-col md:flex-row items-stretch md:items-center space-y-2 md:space-y-0 md:space-x-4 w-full md:w-auto">
          <select 
            value={selectedPeriod} 
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-purple-400 w-full md:w-auto"
          >
            <option value="7">{t('socialMedia.engagement.last7Days')}</option>
            <option value="30">{t('socialMedia.engagement.last30Days')}</option>
            <option value="90">{t('socialMedia.engagement.last90Days')}</option>
          </select>
          <button className="bg-gradient-to-r from-purple-500 to-purple-700 px-4 md:px-6 py-2 rounded-xl text-white font-medium hover:opacity-90 transition-opacity flex items-center justify-center space-x-2 w-full md:w-auto">
            <FontAwesomeIcon icon={faDownload} />
            <span className="hidden md:inline">{t('socialMedia.engagement.exportReport')}</span>
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/70 text-sm">{t('socialMedia.engagement.metrics.totalEngagement')}</p>
              <p className="text-2xl font-bold text-white">1,650</p>
              <p className="text-green-400 text-sm">+15% {t('socialMedia.engagement.metrics.thisMonth')}</p>
            </div>
            <FontAwesomeIcon icon={faComments} className="text-3xl text-purple-400" />
          </div>
        </div>
        <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/70 text-sm">{t('socialMedia.engagement.metrics.avgResponseTime')}</p>
              <p className="text-2xl font-bold text-white">2.3s</p>
              <p className="text-green-400 text-sm">-0.5s {t('socialMedia.engagement.metrics.thisWeek')}</p>
            </div>
            <FontAwesomeIcon icon={faClock} className="text-3xl text-blue-400" />
          </div>
        </div>
        <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/70 text-sm">{t('socialMedia.engagement.metrics.problemSolvingRate')}</p>
              <p className="text-2xl font-bold text-white">%87</p>
              <p className="text-green-400 text-sm">+3% {t('socialMedia.engagement.metrics.thisMonth')}</p>
            </div>
            <FontAwesomeIcon icon={faCheckCircle} className="text-3xl text-green-400" />
          </div>
        </div>
        <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/70 text-sm">{t('socialMedia.engagement.metrics.customerSatisfaction')}</p>
              <p className="text-2xl font-bold text-white">4.2/5</p>
              <p className="text-green-400 text-sm">+0.3 {t('socialMedia.engagement.metrics.thisMonth')}</p>
            </div>
            <FontAwesomeIcon icon={faStar} className="text-3xl text-yellow-400" />
          </div>
        </div>
      </div>

      {/* Analytics Charts Section */}
      <div>
        <h2 className="text-2xl font-bold text-white mb-6">{t('socialMedia.engagement.charts.engagementAnalytics')}</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Response Time Chart */}
          <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6 relative">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-2">
                <h3 className="text-lg font-semibold text-white">{t('socialMedia.engagement.charts.responseTime')}</h3>
                <Tooltip id="response-time-tooltip" content={t('socialMedia.engagement.charts.responseTimeTooltip')}>
                  <FontAwesomeIcon 
                    icon={faInfoCircle} 
                    className="text-white/50 hover:text-white/70 text-sm cursor-help transition-colors"
                    onMouseEnter={() => handleTooltipEnter('response-time-tooltip')}
                    onMouseLeave={handleTooltipLeave}
                  />
                </Tooltip>
              </div>
              <button
                onClick={() => {
                  setModalChart('responseTime');
                  setTimeout(() => createModalChart('responseTime', modalResponseTimeChartRef), 100);
                }}
                className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
              >
                <FontAwesomeIcon icon={faExpand} className="text-white/70 hover:text-white text-sm" />
              </button>
            </div>
            <div className="h-64">
              <canvas ref={responseTimeChartRef}></canvas>
            </div>
            <AIAnalysis chartType="responseTime" />
          </div>

          {/* Problem Solving Chart */}
          <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6 relative">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-2">
                <h3 className="text-lg font-semibold text-white">{t('socialMedia.engagement.charts.problemSolvingRates')}</h3>
                <Tooltip id="problem-solving-tooltip" content={t('socialMedia.engagement.charts.problemSolvingTooltip')}>
                  <FontAwesomeIcon 
                    icon={faInfoCircle} 
                    className="text-white/50 hover:text-white/70 text-sm cursor-help transition-colors"
                    onMouseEnter={() => handleTooltipEnter('problem-solving-tooltip')}
                    onMouseLeave={handleTooltipLeave}
                  />
                </Tooltip>
              </div>
              <button
                onClick={() => {
                  setModalChart('problemSolving');
                  setTimeout(() => createModalChart('problemSolving', modalProblemSolvingChartRef), 100);
                }}
                className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
              >
                <FontAwesomeIcon icon={faExpand} className="text-white/70 hover:text-white text-sm" />
              </button>
            </div>
            <div className="h-64">
              <canvas ref={problemSolvingChartRef}></canvas>
            </div>
            <AIAnalysis chartType="problemSolving" />
          </div>

          {/* Feedback Type Chart */}
          <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6 relative">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-2">
                <h3 className="text-lg font-semibold text-white">{t('socialMedia.engagement.charts.feedbackTypes')}</h3>
                <Tooltip id="feedback-type-tooltip" content={t('socialMedia.engagement.charts.feedbackTypesTooltip')}>
                  <FontAwesomeIcon 
                    icon={faInfoCircle} 
                    className="text-white/50 hover:text-white/70 text-sm cursor-help transition-colors"
                    onMouseEnter={() => handleTooltipEnter('feedback-type-tooltip')}
                    onMouseLeave={handleTooltipLeave}
                  />
                </Tooltip>
              </div>
              <button
                onClick={() => {
                  setModalChart('feedbackType');
                  setTimeout(() => createModalChart('feedbackType', modalFeedbackTypeChartRef), 100);
                }}
                className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
              >
                <FontAwesomeIcon icon={faExpand} className="text-white/70 hover:text-white text-sm" />
              </button>
            </div>
            <div className="h-64">
              <canvas ref={feedbackTypeChartRef}></canvas>
            </div>
            <AIAnalysis chartType="feedbackType" />
          </div>

          {/* Trend Chart */}
          <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6 relative">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-2">
                <h3 className="text-lg font-semibold text-white">{t('socialMedia.engagement.charts.engagementTrends')}</h3>
                <Tooltip id="trend-tooltip" content={t('socialMedia.engagement.charts.trendTooltip')}>
                  <FontAwesomeIcon 
                    icon={faInfoCircle} 
                    className="text-white/50 hover:text-white/70 text-sm cursor-help transition-colors"
                    onMouseEnter={() => handleTooltipEnter('trend-tooltip')}
                    onMouseLeave={handleTooltipLeave}
                  />
                </Tooltip>
              </div>
              <button
                onClick={() => {
                  setModalChart('trend');
                  setTimeout(() => createModalChart('trend', modalTrendChartRef), 100);
                }}
                className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
              >
                <FontAwesomeIcon icon={faExpand} className="text-white/70 hover:text-white text-sm" />
              </button>
            </div>
            <div className="h-64">
              <canvas ref={trendChartRef}></canvas>
            </div>
            <AIAnalysis chartType="trend" />
          </div>
        </div>
      </div>

      {/* Feedbacks Section */}
      <div className="mt-8">
        <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-2">
              <h3 className="text-lg font-semibold text-white">{t('socialMedia.engagement.charts.customerFeedbacks')}</h3>
              <Tooltip id="feedbacks-tooltip" content={t('socialMedia.engagement.charts.feedbacksTooltip')}>
                <FontAwesomeIcon 
                  icon={faInfoCircle} 
                  className="text-white/50 hover:text-white/70 text-sm cursor-help transition-colors"
                  onMouseEnter={() => handleTooltipEnter('feedbacks-tooltip')}
                  onMouseLeave={handleTooltipLeave}
                />
              </Tooltip>
            </div>
          </div>
          
          {state.loading.feedbacks ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-white/70">{t('socialMedia.engagement.charts.feedbacksLoading')}</div>
            </div>
          ) : state.error.feedbacks === 'PREPARING' ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-white/70">{t('socialMedia.engagement.charts.feedbacksPreparing')}</div>
            </div>
          ) : state.error.feedbacks ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-red-400">{t('socialMedia.engagement.charts.feedbacksError')}</div>
            </div>
          ) : state.feedbacks ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Positive Feedbacks */}
              <div className="backdrop-blur-xl bg-green-500/10 border border-green-500/20 rounded-xl p-4">
                <div className="flex items-center space-x-2 mb-4">
                  <FontAwesomeIcon icon={faThumbsUp} className="text-green-400" />
                  <h4 className="text-white font-semibold">{t('socialMedia.engagement.charts.positiveFeedbacks')}</h4>
                </div>
                <div className="space-y-2">
                  {state.feedbacks.positives.length > 0 ? (
                    state.feedbacks.positives.map((feedback, index) => (
                      <div key={index} className="text-white/80 text-sm bg-green-500/10 rounded-lg p-3">
                        "{feedback}"
                      </div>
                    ))
                  ) : (
                    <div className="text-white/50 text-sm">{t('socialMedia.engagement.charts.noPositiveFeedbacks')}</div>
                  )}
                </div>
              </div>

              {/* Negative Feedbacks */}
              <div className="backdrop-blur-xl bg-red-500/10 border border-red-500/20 rounded-xl p-4">
                <div className="flex items-center space-x-2 mb-4">
                  <FontAwesomeIcon icon={faFrown} className="text-red-400" />
                  <h4 className="text-white font-semibold">{t('socialMedia.engagement.charts.negativeFeedbacks')}</h4>
                </div>
                <div className="space-y-2">
                  {state.feedbacks.negatives.length > 0 ? (
                    state.feedbacks.negatives.map((feedback, index) => (
                      <div key={index} className="text-white/80 text-sm bg-red-500/10 rounded-lg p-3">
                        "{feedback}"
                      </div>
                    ))
                  ) : (
                    <div className="text-white/50 text-sm">{t('socialMedia.engagement.charts.noNegativeFeedbacks')}</div>
                  )}
                </div>
              </div>

              {/* Suggestions */}
              <div className="backdrop-blur-xl bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
                <div className="flex items-center space-x-2 mb-4">
                  <FontAwesomeIcon icon={faLightbulb} className="text-blue-400" />
                  <h4 className="text-white font-semibold">{t('socialMedia.engagement.charts.suggestions')}</h4>
                </div>
                <div className="space-y-2">
                  {state.feedbacks.suggestions.length > 0 ? (
                    state.feedbacks.suggestions.map((feedback, index) => (
                      <div key={index} className="text-white/80 text-sm bg-blue-500/10 rounded-lg p-3">
                        "{feedback}"
                      </div>
                    ))
                  ) : (
                    <div className="text-white/50 text-sm">{t('socialMedia.engagement.charts.noSuggestions')}</div>
                  )}
                </div>
              </div>

              {/* Complaints */}
              <div className="backdrop-blur-xl bg-orange-500/10 border border-orange-500/20 rounded-xl p-4">
                <div className="flex items-center space-x-2 mb-4">
                  <FontAwesomeIcon icon={faExclamationCircle} className="text-orange-400" />
                  <h4 className="text-white font-semibold">{t('socialMedia.engagement.charts.complaints')}</h4>
                </div>
                <div className="space-y-2">
                  {state.feedbacks.complaints.length > 0 ? (
                    state.feedbacks.complaints.map((feedback, index) => (
                      <div key={index} className="text-white/80 text-sm bg-orange-500/10 rounded-lg p-3">
                        "{feedback}"
                      </div>
                    ))
                  ) : (
                    <div className="text-white/50 text-sm">{t('socialMedia.engagement.charts.noComplaints')}</div>
                  )}
                </div>
              </div>
              <AIAnalysis chartType="feedbacks" />
            </div>
          ) : (
            <div className="flex items-center justify-center h-64">
              <div className="text-white/50">{t('socialMedia.engagement.charts.noFeedbacksData')}</div>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {modalChart && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setModalChart(null)}></div>
          <div className="absolute top-4 left-4 right-4 bottom-4 md:top-10 md:left-10 md:right-10 md:bottom-10 bg-gray-900 rounded-2xl border border-white/10 overflow-hidden">
            <div className="flex items-center justify-between p-4 md:p-6 border-b border-white/10">
              <h3 className="text-lg md:text-xl font-bold text-white pr-2">
                {modalChart === 'responseTime' && t('socialMedia.engagement.charts.responseTime')}
                {modalChart === 'problemSolving' && t('socialMedia.engagement.charts.problemSolvingRates')}
                {modalChart === 'feedbackType' && t('socialMedia.engagement.charts.feedbackTypes')}
                {modalChart === 'trend' && t('socialMedia.engagement.charts.engagementTrends')}
              </h3>
              <button
                onClick={() => setModalChart(null)}
                className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors flex-shrink-0"
              >
                <FontAwesomeIcon icon={faTimes} className="text-white/70 hover:text-white" />
              </button>
            </div>
            <div className="p-4 md:p-6 h-[calc(100%-60px)] md:h-[calc(100%-80px)]">
              {modalChart === 'responseTime' && <canvas ref={modalResponseTimeChartRef} className="w-full h-full"></canvas>}
              {modalChart === 'problemSolving' && <canvas ref={modalProblemSolvingChartRef} className="w-full h-full"></canvas>}
              {modalChart === 'feedbackType' && <canvas ref={modalFeedbackTypeChartRef} className="w-full h-full"></canvas>}
              {modalChart === 'trend' && <canvas ref={modalTrendChartRef} className="w-full h-full"></canvas>}
            </div>
          </div>
        </div>
      )}

      {/* AI Analysis Modal */}
      {aiModalOpen && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setAiModalOpen(false)}></div>
          <div className="absolute top-4 left-4 right-4 bottom-4 sm:top-10 sm:left-10 sm:right-10 sm:bottom-10 md:top-1/2 md:left-1/2 md:right-auto md:bottom-auto md:-translate-x-1/2 md:-translate-y-1/2 bg-gray-900 rounded-2xl border border-white/10 w-full max-w-md overflow-hidden">
            <div className="p-4 sm:p-6 border-b border-white/10">
              <h3 className="text-lg sm:text-xl font-bold text-white">{t('socialMedia.engagement.aiAnalysisTitle')}</h3>
            </div>
            <div className="p-4 sm:p-6 space-y-4">
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">{t('socialMedia.engagement.startDate') || 'Başlangıç Tarihi'}</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    max={endDate || new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">{t('socialMedia.engagement.endDate') || 'Bitiş Tarihi'}</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    min={startDate}
                    max={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                  />
                </div>
              </div>
              <div className="text-white/80">
                {t('socialMedia.engagement.estimatedCreditsLabel')}
                <div className="mt-2 text-xl sm:text-2xl font-semibold text-white">
                  {estimateLoading ? (
                    <span className="flex items-center">
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                      {t('socialMedia.engagement.calculating')}
                    </span>
                  ) : (
                    estimatedCredits !== null 
                      ? Number(estimatedCredits).toLocaleString(i18n.language === 'tr' ? 'tr-TR' : 'en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
                      : '—'
                  )}
                </div>
              </div>
              <button
                className="w-full bg-gradient-to-r from-blue-500 to-blue-700 py-2 px-4 rounded-xl text-white font-medium hover:from-blue-600 hover:to-blue-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!startDate || !endDate}
                onClick={async () => {
                  if (!startDate || !endDate) return;
                  setEstimateLoading(true);
                  setEstimatedCredits(null);
                  try {
                    const res = await creditsAPI.estimate('socialmediaengagement', startDate, endDate);
                    const value = res?.data?.estimatedCredits ?? res?.data?.data?.estimatedCredits ?? null;
                    setEstimatedCredits(typeof value === 'number' ? value : null);
                  } finally {
                    setEstimateLoading(false);
                  }
                }}
              >
                {t('socialMedia.engagement.calculateCredits') || 'Kredi Hesapla'}
              </button>
              <button
                className="w-full bg-gradient-to-r from-purple-500 to-purple-700 py-3 px-6 rounded-xl text-white font-semibold hover:from-purple-600 hover:to-purple-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={estimateLoading || estimatedCredits === null || !startDate || !endDate}
                onClick={async () => {
                  try {
                    if (!startDate || !endDate) return;
                    setEstimateLoading(true);
                    await analysisJobsAPI.start('socialmediaengagement', startDate, endDate);
                  } finally {
                    setEstimateLoading(false);
                    setAiModalOpen(false);
                  }
                }}
              >
                {t('socialMedia.engagement.spendCredits')}
              </button>
              <button
                className="w-full mt-2 bg-white/10 border border-white/20 py-3 px-6 rounded-xl text-white hover:bg-white/20 transition"
                onClick={() => {
                  setAiModalOpen(false);
                  setStartDate('');
                  setEndDate('');
                  setEstimatedCredits(null);
                }}
              >
                {t('socialMedia.engagement.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EngagementPage;
